import { Icon } from "@/components/ui/Icon";
import { useNotificationDefaults } from "@/hooks/useNotificationDefaults";
import { t } from "@/lib/strings";
import type { NotificationDefaults } from "@/lib/types";

// flow-admin only. Sets the org-wide default matrix (the starting point every
// user inherits) and, per reason, whether the E-Mail is obligatory (locked → on
// for everyone, un-disableable). In-app stays always-on; E-Mail is the control.
const isEmailOn = (d: NotificationDefaults, reason: string) => Boolean(d.matrix[reason]?.email);
const isEmailLocked = (d: NotificationDefaults, reason: string) => Boolean(d.locked[reason]?.email);

function setEmailDefault(d: NotificationDefaults, reason: string, value: boolean) {
  return {
    ...d,
    matrix: { ...d.matrix, [reason]: { ...(d.matrix[reason] ?? {}), email: value } },
  };
}

function setEmailLock(d: NotificationDefaults, reason: string, value: boolean) {
  return {
    ...d,
    locked: { ...d.locked, [reason]: { ...(d.locked[reason] ?? {}), email: value } },
    // Locking implies the default is on.
    matrix: value
      ? { ...d.matrix, [reason]: { ...(d.matrix[reason] ?? {}), email: true } }
      : d.matrix,
  };
}

export function AdminNotificationDefaults() {
  const { query, save } = useNotificationDefaults();
  const d = query.data;

  if (query.isLoading || !d) {
    return <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{t.loading}</div>;
  }

  return (
    <div>
      <p style={{ fontSize: 12.5, color: "var(--ink-3)", margin: "0 0 12px", lineHeight: 1.5 }}>
        {t.adminNotifDefaultsSub}
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ color: "var(--ink-3)", textAlign: "left" }}>
            <th style={{ padding: "6px 8px" }} />
            <th style={{ padding: "6px 8px", width: 80 }}>{t.notifPrefsColInApp}</th>
            <th style={{ padding: "6px 8px", width: 120 }}>{t.notifPrefsColEmail}</th>
          </tr>
        </thead>
        <tbody>
          {d.reasons.map((reason) => {
            const locked = isEmailLocked(d, reason);
            return (
              <tr key={reason} style={{ borderTop: "1px solid var(--hairline-soft)" }}>
                <td style={{ padding: "8px", color: "var(--ink-1)" }}>
                  {t.notifReasonLabels[reason] ?? reason}
                </td>
                <td style={{ padding: "8px" }}>
                  <input type="checkbox" checked readOnly disabled />
                </td>
                <td style={{ padding: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={locked || isEmailOn(d, reason)}
                      disabled={locked}
                      onChange={(e) => save.mutate(setEmailDefault(d, reason, e.target.checked))}
                    />
                    <button
                      type="button"
                      title={locked ? t.notifObligatory : t.notifMakeObligatory}
                      aria-pressed={locked}
                      onClick={() => save.mutate(setEmailLock(d, reason, !locked))}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 2,
                        borderRadius: 5,
                        opacity: locked ? 1 : 0.55,
                      }}
                    >
                      <Icon
                        name="lock"
                        size={14}
                        color={locked ? "var(--ink-1)" : "var(--ink-4)"}
                      />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
